/**
 * flightFollowing — shared overdue / flight-following helpers for the
 * refactored calendar views (month, day; week when it grows blocks that need
 * it).
 *
 * LEGACY PARITY (`calendar-day.event-marking.js:90-100`,
 * `dispatch/event-status.js:39-56`): the feature is gated on the org setting
 * `organizationSettings.enableTrackFlight === '1'`. When enabled, dispatch
 * check-out records the takeoff time and the server computes
 * `expectedArrivalTime` (takeoff + planned duration) and an `overdue` flag on
 * the event; a checked-out flight past its expected arrival is flagged red on
 * the schedule. The client ALSO derives overdue live from
 * `expectedArrivalTime` so blocks turn red between polls.
 *
 * PURE: callers supply `nowSec` — no wall clock in here.
 *
 * @module flightFollowing
 */

/** Truthy for the API's mixed bool/'1'/1 flag encodings. */
const flag = v => v === true || v === 1 || v === '1';

/** The org's flight-following (track flight) feature switch. */
export function isFlightFollowingEnabled(organizationSettings) {
	return flag(organizationSettings?.enableTrackFlight);
}

/**
 * Flight following: is this event an OVERDUE flight?
 *   - never for maintenance;
 *   - a truthy server `overdue` (or view-computed `_overdue`) flag wins;
 *   - otherwise derived live: checked out (inFlight, not completed) and now
 *     past `expectedArrivalTime`.
 *
 * NOTE: callers own the org gate — render as overdue only when
 * `isFlightFollowingEnabled(...)` is also true.
 *
 * @param {Object} event
 * @param {number} nowSec Unix seconds "now".
 */
export function isOverdueEvent(event, nowSec) {
	if (!event) return false;
	const type = String(event.resourceType || event.type || '').toLowerCase();
	if (type.includes('maint')) return false;
	if (flag(event.overdue) || flag(event._overdue)) return true;
	if (!flag(event.inFlight) || flag(event.completed)) return false;
	// No recorded ETA → the scheduled finish is the expectation (matches the
	// server: the day endpoint sends overdue=true for a checked-out flight
	// past finishTime with expectedArrivalTime null; the MONTH endpoint sends
	// neither field — backend PR backlog — so this derivation carries month).
	const eta = Number(event.expectedArrivalTime) || Number(event.finishTime);
	return Number.isFinite(eta) && eta > 0 && Number(nowSec) > eta;
}

export default isOverdueEvent;
